import { Sentry } from './lib/sentry'
import { AuthProvider } from './context/AuthContext'
import AuthGate from './components/AuthGate'
import { SettingsProvider } from './context/SettingsContext'
import { SubscriptionProvider } from './context/SubscriptionContext'
import { FinanceProvider, useFinance } from './context/FinanceContext'
import { PAGES } from './lib/constants'
import Sidebar from './components/layout/Sidebar'
import Header from './components/layout/Header'
import Dashboard from './pages/Dashboard'
import IncomeCareer from './pages/IncomeCareer'
import FinancialHealth from './pages/FinancialHealth'
import LoansDebt from './pages/LoansDebt'
import LifeDecisions from './pages/LifeDecisions'
import InvestingRetirement from './pages/InvestingRetirement'
import Settings from './pages/Settings'
import Pricing from './pages/Pricing'

function SentryFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-page px-4">
      <div className="text-center max-w-md">
        <h2 className="text-xl font-bold text-heading mb-2">Something went wrong</h2>
        <p className="text-muted text-sm mb-4">An unexpected error occurred. Please reload the page.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 rounded-xl bg-cyan-400 text-gray-900 font-semibold hover:bg-cyan-300 transition-colors"
        >
          Reload Page
        </button>
      </div>
    </div>
  )
}

function AppContent() {
  const { state } = useFinance()

  const themeClass = state.theme === 'dark' ? 'dark' : 'light'
  const isDark = state.theme === 'dark'

  const Page = {
    [PAGES.DASHBOARD]: Dashboard,
    [PAGES.INCOME_CAREER]: IncomeCareer,
    [PAGES.FINANCIAL_HEALTH]: FinancialHealth,
    [PAGES.LOANS_DEBT]: LoansDebt,
    [PAGES.LIFE_DECISIONS]: LifeDecisions,
    [PAGES.INVESTING_RETIREMENT]: InvestingRetirement,
    [PAGES.SETTINGS]: Settings,
    [PAGES.PRICING]: Pricing,
  }[state.page] || Dashboard

  return (
    <div className={themeClass}>
      <div className="min-h-screen bg-page text-heading">
        {/* Mesh gradient background */}
        <div className="fixed inset-0 pointer-events-none" style={{
          background: isDark
            ? `radial-gradient(ellipse at 20% 50%, rgba(34,211,238,0.07) 0%, transparent 50%),
               radial-gradient(ellipse at 80% 20%, rgba(59,130,246,0.06) 0%, transparent 50%),
               radial-gradient(ellipse at 50% 80%, rgba(6,182,212,0.05) 0%, transparent 50%)`
            : `radial-gradient(ellipse at 20% 50%, rgba(8,145,178,0.07) 0%, transparent 50%),
               radial-gradient(ellipse at 80% 20%, rgba(37,99,235,0.05) 0%, transparent 50%),
               radial-gradient(ellipse at 50% 80%, rgba(13,148,136,0.04) 0%, transparent 50%)`
        }} />

        {/* Floating decorative circles */}
        <div className={`fixed top-20 left-20 w-72 h-72 rounded-full blur-3xl pointer-events-none animate-pulse-slow ${
          isDark ? 'bg-cyan-500/5' : 'bg-cyan-400/5'
        }`} />
        <div className={`fixed bottom-20 right-20 w-96 h-96 rounded-full blur-3xl pointer-events-none animate-pulse-slow ${
          isDark ? 'bg-blue-500/5' : 'bg-blue-400/5'
        }`} style={{ animationDelay: '1.5s' }} />
        <div className={`fixed top-1/2 left-1/2 w-64 h-64 rounded-full blur-3xl pointer-events-none animate-pulse-slow ${
          isDark ? 'bg-teal-400/5' : 'bg-teal-300/5'
        }`} style={{ animationDelay: '3s' }} />

        <Sidebar />
        <Header />

        <main className={`pt-14 lg:pt-0 min-h-screen relative transition-all duration-300 ease-out ${
          state.sidebarCollapsed ? 'lg:ml-[88px]' : 'lg:ml-[276px]'
        }`}>
          <div className="p-6 lg:p-8 max-w-7xl mx-auto">
            <Page />
          </div>
        </main>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<SentryFallback />}>
      <AuthProvider>
        <AuthGate>
          <SettingsProvider>
            <SubscriptionProvider>
              <FinanceProvider>
                <AppContent />
              </FinanceProvider>
            </SubscriptionProvider>
          </SettingsProvider>
        </AuthGate>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  )
}
